import { useCallback, useEffect, useRef } from "react"

interface UseInfiniteScrollOptions {
  hasNextPage: boolean
  loading: boolean
  onLoadMore: () => void
  observerPreloadDistance?: number
}

export const useInfiniteScroll = ({
  hasNextPage,
  loading,
  onLoadMore,
  observerPreloadDistance = 200,
}: UseInfiniteScrollOptions) => {
  const observerRef = useRef<HTMLDivElement>(null)

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries
      if (entry.isIntersecting && hasNextPage && !loading) {
        onLoadMore()
      }
    },
    [hasNextPage, loading, onLoadMore]
  )

  useEffect(() => {
    const observer = observerRef.current
    if (!observer) return

    const intersectionObserver = new IntersectionObserver(handleIntersection, {
      rootMargin: `${observerPreloadDistance}px`,
    })

    intersectionObserver.observe(observer)

    return () => intersectionObserver.disconnect()
  }, [handleIntersection, observerPreloadDistance])

  return observerRef
}
